// @givmo/board — the meeting's attachments, and the two-step signed-URL flow that adds one.
//
// ══ WHY ATTACHMENTS ARE A MEETING'S, NOT A PACKET VERSION'S ═════════════════════════════════════
//
// `board_packet_document` is scoped to the MEETING. A treasurer's report uploaded for version 1 is
// still there for version 3, so re-publishing never forces a re-upload — which is why this panel sits
// beside the versions rather than inside one, and why removing an ENTRY from a draft does not delete
// the file it pointed at.
//
// ══ THE UPLOAD IS THREE STEPS, AND THE MIDDLE ONE IS NOT OUR SERVER ═════════════════════════════
//
//   1. ISSUE — ask the backend for a signed PUT URL for a named file and content type. The object key
//      is minted SERVER-side under the organization's own `board/…/meetings/{meeting}/documents/{uuid}`; the caller
//      never chooses where bytes land, and confirm later refuses any reference outside that prefix.
//   2. PUT — the bytes go straight to storage from the browser. The signed URL is BOUND to the content
//      type it was issued for, so the `Content-Type` header must match what was asked for or storage
//      itself rejects it. This request does not carry our credentials and is not the api-client's.
//   3. CONFIRM — tell the backend the object is there. It checks the object ACTUALLY EXISTS before
//      recording the row and reads `byte_size` off the stored object, so a forged reference cannot
//      become an attachment. This is what makes the two-step shape worth its extra round trip.
//
// A failure at step 2 or 3 leaves a `pending_upload` row rather than a lie: the row says bytes were
// asked for and never confirmed. The list renders that state as what it is.
//
// ══ THE DIGEST IS SELF-REPORTED, AND THE COPY SAYS SO ═══════════════════════════════════════════
//
// `reported_content_digest` is named for its provenance: this browser computed it, the backend stores
// it without ever streaming the object to check it, and a later item that binds an attachment into an
// approved record needs the artifact's own fingerprint rather than its location. So it is computed
// where the platform offers it (`crypto.subtle` needs a secure context) and simply OMITTED where it
// does not — an upload must never fail over a field nothing checks. Nothing on screen claims it proves
// anything about the file.
//
// ══ NO BUCKET CONFIGURED IS A STATE, NOT A CRASH ════════════════════════════════════════════════
//
// With `BOARD_DOCUMENTS_BUCKET_NAME` empty the two attachment routes answer 503 at CALL time and every
// other packet surface keeps working. That is every environment without the bucket provisioned, so the
// panel says exactly that and stops offering an action that cannot succeed — while the versions, the
// entries, publication and distribution beside it carry on.

import { useRef, useState } from 'react';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';

import { isGivmoError, type BoardPacketDocumentOut } from '@givmo/api-client';
import { Badge, Button, Card, CardBody, ErrorMessage, Skeleton, toast } from '@givmo/design-system';
import { FieldNote } from '../../components/field-note';
import {
  ATTACHMENT_ACCEPT,
  displayInstant,
  displaySize,
  extractDocuments,
  isAllowedAttachment,
  isStorageUnavailable,
  isUploaded,
} from './packets-decode';
import { useBoardOperations } from '../../board-runtime';
import { useBoardPermission } from '../../board-host';

/** CBG-FIX14 — the panel's words when files can't be stored here, with no infrastructure term. */
export const ATTACHMENTS_UNAVAILABLE_TITLE = 'Files can’t be added here right now';
export const ATTACHMENTS_UNAVAILABLE_DETAIL =
  'Files already attached can’t be opened here for now, either. Everything else about the packet — its contents, issuing a version, and sending it to the board — works normally.';

export interface AttachmentsPanelProps {
  orgRef: string;
  meetingId: string;
  /** Raised when storage answers 503, so the page can stop offering attachment entries too. */
  onStorageUnavailable?: () => void;
}

/**
 * A SHA-256 of the file's bytes, as lowercase hex — or null where the platform will not compute one.
 *
 * `crypto.subtle` is undefined outside a secure context (and in a jsdom test), which is a fine reason
 * to send no digest and no reason at all to fail an upload.
 */
async function reportedDigest(file: File): Promise<string | null> {
  try {
    const subtle = globalThis.crypto?.subtle;
    if (subtle === undefined) return null;
    const digest = await subtle.digest('SHA-256', await file.arrayBuffer());
    return [...new Uint8Array(digest)].map((byte) => byte.toString(16).padStart(2, '0')).join('');
  } catch {
    return null;
  }
}

export function AttachmentsPanel({
  orgRef,
  meetingId,
  onStorageUnavailable,
}: AttachmentsPanelProps) {
  const operations = useBoardOperations();
  const queryClient = useQueryClient();
  // Attaching a file is `board_meeting:write`; without it, the panel lists the files and no more.
  const canAttach = useBoardPermission('board_meeting:write');
  const inputRef = useRef<HTMLInputElement | null>(null);
  const [storageUnavailable, setStorageUnavailable] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const documentsOptions = operations.officerListBoardDocumentsOptions({
    path: { org_ref: orgRef, meeting_id: meetingId },
  });
  const documentsQuery = useQuery({ ...documentsOptions, select: extractDocuments });
  const attachments = documentsQuery.data ?? [];

  function noteStorageUnavailable() {
    setStorageUnavailable(true);
    setError(null);
    onStorageUnavailable?.();
  }

  const upload = useMutation({
    mutationFn: async (file: File): Promise<BoardPacketDocumentOut> => {
      const contentType = file.type;
      const path = { org_ref: orgRef, meeting_id: meetingId };

      // 1. Ask for a place to put it. The key is minted server-side; we only echo it back.
      const issued = await operations.officerIssueBoardDocumentUploadUrl({
        path,
        body: { content_type: contentType, file_name: file.name },
        throwOnError: true,
      });

      // 2. The bytes go straight to storage. NOT through the api-client: this request carries no
      //    Givmo credential, and the signed URL is bound to the content type it was issued for.
      //    ⚠ BOTH headers below are part of the V4 signature: the backend signer bakes
      //    `Content-Disposition: attachment` in (identity.generate_board_document_upload_url), and
      //    every signed header lands in the canonical request — omitting one is a 403
      //    SignatureDoesNotMatch that reads like a permissions problem.
      const stored = await fetch(issued.data.upload_url, {
        method: 'PUT',
        headers: {
          'Content-Type': issued.data.content_type,
          'Content-Disposition': 'attachment',
        },
        body: file,
      });
      if (!stored.ok) {
        throw new Error(`The file couldn’t be stored (${stored.status}).`);
      }

      // 3. Only now does a row exist. The backend checks the object is really there first.
      const digest = await reportedDigest(file);
      const confirmed = await operations.officerConfirmBoardDocument({
        path,
        body: {
          object_ref: issued.data.object_ref,
          file_name: file.name,
          content_type: issued.data.content_type,
          ...(digest !== null ? { reported_content_digest: digest } : {}),
        },
        throwOnError: true,
      });
      return confirmed.data;
    },
    onSuccess: async (attachment) => {
      setError(null);
      await queryClient.invalidateQueries({ queryKey: documentsOptions.queryKey });
      toast.success(`Attached ${attachment.file_name}.`);
    },
    onError: (caught) => {
      if (isStorageUnavailable(caught)) {
        noteStorageUnavailable();
        return;
      }
      setError(
        isGivmoError(caught)
          ? caught.message
          : caught instanceof Error
            ? caught.message
            : 'We couldn’t attach that file. Please try again.',
      );
    },
  });

  const download = useMutation({
    mutationFn: async (attachment: BoardPacketDocumentOut) => {
      const { data } = await operations.officerGetBoardDocumentDownloadUrl({
        path: { org_ref: orgRef, meeting_id: meetingId, document_id: attachment.id },
        throwOnError: true,
      });
      return data;
    },
    onSuccess: (link) => {
      // A read is its own short-lived signed GET, minted per request and never persisted — so it is
      // opened rather than stored anywhere in this app.
      window.open(link.download_url, '_blank', 'noopener,noreferrer');
    },
    onError: (caught) => {
      if (isStorageUnavailable(caught)) {
        noteStorageUnavailable();
        return;
      }
      toast.error(
        isGivmoError(caught) ? caught.message : 'We couldn’t open that file. Please try again.',
      );
    },
  });

  function onPicked(file: File | null) {
    if (file === null) return;
    setError(null);
    if (!isAllowedAttachment(file.type)) {
      setError(
        'That kind of file can’t go in a packet. Attach a PDF, or a photo or scan as JPEG, PNG, HEIC or TIFF.',
      );
      return;
    }
    upload.mutate(file);
  }

  return (
    <Card testId="packet-attachments">
      <CardBody className="flex flex-col gap-16">
        <div className="flex flex-wrap items-center justify-between gap-12">
          <h2 className="font-sans text-s15 font-w600 tracking-givmo text-text-strong">
            Files for this meeting
          </h2>
          {!storageUnavailable && canAttach ? (
            <>
              <input
                ref={inputRef}
                type="file"
                accept={ATTACHMENT_ACCEPT}
                className="sr-only"
                onChange={(event) => {
                  onPicked(event.target.files?.[0] ?? null);
                  // Clear it, so picking the SAME file again still fires a change event.
                  event.target.value = '';
                }}
                data-testid="packet-attachment-input"
              />
              <Button
                appearance="saas"
                tone="secondary"
                size="sm"
                loading={upload.isPending}
                onClick={() => inputRef.current?.click()}
                testId="packet-attachment-add"
              >
                Attach a file
              </Button>
            </>
          ) : null}
        </div>

        <FieldNote>
          {canAttach
            ? 'Files belong to the meeting, not to one version of the packet — attach a treasurer’s report once and every version can include it.'
            : 'Files belong to the meeting, not to one version of the packet.'}
        </FieldNote>

        {storageUnavailable ? (
          <div
            className="flex flex-col gap-8 rounded-lg border border-border-subtle bg-surface-sunken p-16"
            data-testid="packet-attachments-storage-unavailable"
          >
            {/* CBG-FIX14: what the officer can and can't do, with no infrastructure term — the
                state is "this environment has no document storage configured", which is Givmo's
                setting and nothing the officer can see or change. */}
            <span className="font-sans text-s14 font-w600 tracking-givmo text-text-strong">
              {ATTACHMENTS_UNAVAILABLE_TITLE}
            </span>
            <span className="font-sans text-s13 font-w400 leading-h15 tracking-givmo text-text-secondary">
              {ATTACHMENTS_UNAVAILABLE_DETAIL}
            </span>
          </div>
        ) : null}

        {error !== null ? (
          <ErrorMessage testId="packet-attachment-error">{error}</ErrorMessage>
        ) : null}

        {documentsQuery.isPending ? (
          <Skeleton className="h-48 w-full rounded-lg" testId="packet-attachments-loading" />
        ) : null}

        {documentsQuery.isError ? (
          <div className="flex flex-col items-start gap-10" data-testid="packet-attachments-error">
            <p className="font-sans text-s14 font-w400 tracking-givmo text-text-secondary">
              We couldn’t load this meeting’s files.
            </p>
            <Button
              appearance="saas"
              tone="secondary"
              size="sm"
              onClick={() => void documentsQuery.refetch()}
              testId="packet-attachments-retry"
            >
              Try again
            </Button>
          </div>
        ) : null}

        {documentsQuery.isSuccess && attachments.length === 0 ? (
          <p
            className="font-sans text-s14 font-w400 leading-h15 tracking-givmo text-text-secondary"
            data-testid="packet-attachments-empty"
          >
            Nothing attached to this meeting yet.
          </p>
        ) : null}

        {attachments.length > 0 ? (
          <ul className="flex flex-col gap-8" data-testid="packet-attachment-list">
            {attachments.map((attachment) => {
              const size = displaySize(attachment.byte_size);
              return (
                <li
                  key={attachment.id}
                  className="flex flex-wrap items-center justify-between gap-12 rounded-lg border border-border-subtle p-12"
                  data-testid={`packet-attachment-${attachment.id}`}
                >
                  <div className="flex min-w-0 flex-col gap-2">
                    <span className="truncate font-sans text-s14 font-w600 tracking-givmo text-text-strong">
                      {attachment.file_name}
                    </span>
                    <span className="font-sans text-s12 font-w400 tracking-givmo text-text-tertiary">
                      {size !== null ? `${size} · ` : ''}
                      {displayInstant(attachment.uploaded_at ?? attachment.created_at)}
                    </span>
                  </div>
                  <div className="flex shrink-0 items-center gap-10">
                    {isUploaded(attachment) ? (
                      !storageUnavailable ? (
                        <Button
                          appearance="saas"
                          tone="secondary"
                          size="sm"
                          loading={download.isPending && download.variables?.id === attachment.id}
                          onClick={() => download.mutate(attachment)}
                          testId={`packet-attachment-open-${attachment.id}`}
                        >
                          Open
                        </Button>
                      ) : null
                    ) : (
                      // A row whose bytes never landed. Saying so beats an "Open" that 404s.
                      <Badge tone="warning">Upload didn’t finish</Badge>
                    )}
                  </div>
                </li>
              );
            })}
          </ul>
        ) : null}
      </CardBody>
    </Card>
  );
}
